import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Alert, Checkbox } from "@mui/material";
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  TextField,
  Typography,
  FormControlLabel,
  FormLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import Contacts from "./contacts";
import FilterPanel from "./filterPanel";
import CustomFieldInput from "./customFieldInput";
import { Box } from "@mui/system";
import ConfirmDelete from "./confirmDelete";
import {
  AddCircleOutline,
  DeleteOutline,
  EditOutlined,
  Minimize,
  OpenInNew,
  SettingsOutlined,
  Tune,
} from "@mui/icons-material";

import {
  changeCID,
  setCIDS,
  setData,
  setContact,
  setContacts,
  setParcelID,
  setOwnerName,
  setOwnerMailing,
  setBuildingAddr,
  setAssessorCIDValue,
  setExempt,
  setPropertyDesc,
  setSearchStatus,
  resetForm,
  setMarker,
  setCustomFieldValue,
} from "../features/assessorSlice";
import { loadCIDIntoAssessor } from "../services/assessorLoad";
import { addProperty } from "../services/lookupService";
import { notifyService } from "../services/notifyService";
import { getCounties } from "../services/settingsService";
import { setCounties } from "../features/settingsSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CreateCID from "./createCID";
import {
  getCIDProperties,
  getCIDS,
  getCIDContacts,
} from "../services/cidService";
import { useDispatch, useSelector } from "react-redux";
import { updateProperty } from "../services/propertyService";
import { resetContacts } from "../features/contactSlice";
import { deleteCID } from "../services/cidService";
import ModifyCID from "./modifyCID";

import { markerOptionsWithLabels, NO_MARKER_COLOR } from "../markerColors";
import { joinContactsToProperties } from "../utils/propertyJoin";
import {
  persistSelectedCid,
  persistSelectedProperty,
} from "../utils/assessorPersistence";
import {
  EDITOR_FIELDS,
  readHiddenFields,
  writeHiddenFields,
} from "../utils/editorFieldPrefs";
import { canWriteCid } from "../utils/permissions";
import { MapsProvider } from "./mapsProvider";
import AddressField from "./addressField";
import PropertyMap from "./propertyMap";
import { setCoordinates } from "../features/assessorSlice";

const EMPTY_MARKER = {
  value: "",
  color: NO_MARKER_COLOR,
  label: "No marker",
};

// Canonical, stable-key-order snapshot of the editable form fields. Comparing
// two signatures tells whether the editor has unsaved changes (drives the save
// button's disabled state). Custom-field keys are sorted so their insertion
// order never registers as a change.
const formSignature = (form) =>
  JSON.stringify({
    parcelID: form.parcelID ?? "",
    ownerName: form.ownerName ?? "",
    ownerMailing: form.ownerMailing ?? "",
    buildingAddress: form.buildingAddress ?? "",
    assessorValue: form.assessorValue ?? "",
    cidValue: form.cidValue ?? "",
    propertyDesc: form.propertyDesc ?? "",
    exempt: !!form.exempt,
    flagColor: form.flagColor ?? null,
    contactID: form.contactID ?? null,
    contactIDs: [...(form.contactIDs || [])].sort((a, b) => a - b),
    latitude: form.latitude ?? null,
    longitude: form.longitude ?? null,
    customFields: Object.fromEntries(
      Object.entries(form.customFields || {}).sort(([a], [b]) =>
        a.localeCompare(b)
      )
    ),
  });

const LookupTool = (props) => {
  const [dialogOpen, toggleDialog] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [confirmDialog, toggleConfirmDialog] = useState(false);
  const [modifyDialog, toggleModifyDialog] = useState(false);
  const [cidActionsAnchorEl, setCidActionsAnchorEl] = useState(null);
  const cidActionsOpen = Boolean(cidActionsAnchorEl);
  // Per-user "Customize fields" menu: which editor fields this user has hidden
  // (persisted in localStorage, applied across all CIDs).
  const [fieldsMenuAnchorEl, setFieldsMenuAnchorEl] = useState(null);
  const fieldsMenuOpen = Boolean(fieldsMenuAnchorEl);
  const [hiddenFields, setHiddenFields] = useState(() => readHiddenFields());
  const hiddenFieldSet = useMemo(() => new Set(hiddenFields), [hiddenFields]);
  const isFieldVisible = useCallback(
    (key) => !hiddenFieldSet.has(key),
    [hiddenFieldSet]
  );
  const searchStatus = useSelector(
    (state) => state.assessor.value.searchStatus
  );
  const dispatch = useDispatch();
  const form = useSelector((state) => state.assessor.value.form);
  const assessor = useSelector((state) => state.assessor.value);

  // Track whether the open property has unsaved edits so the save button can
  // stay disabled until something actually changes. The pristine baseline is
  // captured the first render the editor is shown for a given property
  // (`searchStatus === 2` — the load path sets that flag last, so the form is
  // fully populated by then). Editing changes `formSig` but not the property
  // key, so the baseline is preserved and the diff reveals the change.
  const formSig = useMemo(() => formSignature(form), [form]);
  const baselineFormRef = useRef({ key: null, sig: null });
  if (searchStatus === 2 && baselineFormRef.current.key !== form.transferID) {
    baselineFormRef.current = { key: form.transferID, sig: formSig };
  }
  const isDirty =
    searchStatus === 2 &&
    baselineFormRef.current.sig !== null &&
    baselineFormRef.current.sig !== formSig;
  const auth = useSelector((state) => state.auth.value);
  const admin = auth.admin;
  const cidWritable = canWriteCid(auth, form.cid);
  const counties = useSelector((state) => state.settings.value.counties);
  const legend = useSelector((state) => state.cid.value.legend);
  const propertyFieldDefs = useSelector((state) => state.settings.value.propertyFields);
  const propertyFields = useMemo(
    () => propertyFieldDefs.filter((f) => !f.archived),
    [propertyFieldDefs]
  );

  const markerOptions = markerOptionsWithLabels(legend);

  // On refresh the persisted CID hydrates `form.cid` before the CID list
  // finishes loading, so the Select briefly holds a value with no matching
  // option — which MUI floods the console about. Fall back to "" until the
  // option actually exists (the display re-syncs once the list arrives).
  const cidOptions = assessor.cids || [];
  const cidSelectValue = cidOptions.some((c) => c.id === form.cid)
    ? form.cid
    : "";

  const assessorUrl = useMemo(() => {
    const cidRow = assessor.cids?.find((c) => c.id === form.cid);
    if (!cidRow) {
      return null;
    }
    const county = counties.find((c) => c.code === cidRow.county);
    return county?.assessor_url || null;
  }, [assessor.cids, form.cid, counties]);

  const openCidActions = (event) => {
    setCidActionsAnchorEl(event.currentTarget);
  };

  const closeCidActions = () => {
    setCidActionsAnchorEl(null);
  };

  const openFieldsMenu = (event) => {
    setFieldsMenuAnchorEl(event.currentTarget);
  };

  const closeFieldsMenu = () => {
    setFieldsMenuAnchorEl(null);
  };

  // Toggle one field's visibility. The menu stays open so several fields can be
  // flipped in one pass; each change is written straight to localStorage.
  const toggleField = (key) => {
    setHiddenFields((prev) =>
      writeHiddenFields(
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      )
    );
  };

  const showAllFields = () => {
    setHiddenFields(writeHiddenFields([]));
  };

  const handleChangeCID = async (e) => {
    setDataLoading(true);
    try {
      await loadCIDIntoAssessor(dispatch, e.target.value, assessor.cids);
    } finally {
      setDataLoading(false);
    }
  };

  // Re-fetch this CID's properties + contacts and re-join them into the table
  // data. Called after a contact mutation (e.g. enrichment) from the embedded
  // picker so the Quick Assessor table reflects the change without a full CID
  // reload. Mirrors the join onPush performs after a property save.
  const refreshAssessorData = useCallback(async () => {
    if (!form.cid) {
      return;
    }
    const [refreshProperties, resContacts] = await Promise.all([
      getCIDProperties(form.cid),
      getCIDContacts(form.cid),
    ]);
    dispatch(
      setData(joinContactsToProperties(refreshProperties?.data, resContacts))
    );
  }, [form.cid, dispatch]);

  const onPush = async () => {
    if (form.transferID === -1) {
      addProperty(form).then(async () => {
        const refreshProperties = await getCIDProperties(form.cid);
        const resContacts = await getCIDContacts(form.cid);
        dispatch(setData(joinContactsToProperties(refreshProperties?.data, resContacts)));
        // The just-saved values become the new pristine baseline (forces the
        // save button back to disabled until the next edit).
        baselineFormRef.current = { key: null, sig: null };
        setDataLoading(false);
      });
    } else {
      updateProperty(form).then(async () => {
        const refreshProperties = await getCIDProperties(form.cid);
        const resContacts = await getCIDContacts(form.cid);
        dispatch(setData(joinContactsToProperties(refreshProperties?.data, resContacts)));
        baselineFormRef.current = { key: null, sig: null };
        setDataLoading(false);
      });
    }
  };

  const handleDelete = async () => {
    toggleConfirmDialog(false);
    return new Promise((res) => {
      dispatch(resetForm());
      dispatch(changeCID({ cid: "", data: [] }));
      // The remembered workspace pointed at the CID being deleted — forget it
      // so a refresh doesn't try to reload a dead CID.
      persistSelectedCid(null);
      persistSelectedProperty(null);
      res(deleteCID(form.cid));
    })
      .then(async () => {
        const cids = await getCIDS();
        dispatch(setCIDS(cids));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const startManualEntry = () => {
    if (form.cid === "") {
      notifyService("Select a CID first");
      return;
    }
    dispatch(setSearchStatus(2));
    dispatch(setOwnerName(""));
    dispatch(setOwnerMailing(""));
    dispatch(setBuildingAddr(""));
    dispatch(setPropertyDesc(""));
    dispatch(setContact(null));
    dispatch(setContacts([]));
    dispatch(resetContacts());
    dispatch(setAssessorCIDValue(0));
  };

  useEffect(() => {
    async function reqCIDS() {
      const res = await getCIDS();
      if (res) {
        dispatch(setCIDS(res));
      }
    }
    reqCIDS();
  }, [dispatch]);

  useEffect(() => {
    if (counties.length) {
      return;
    }
    getCounties().then((res) => {
      if (res) {
        dispatch(setCounties(res));
      }
    });
  }, [counties.length, dispatch]);

  return (
    <MapsProvider>
    <Grid
      container
      spacing={1.5}
      textAlign="center"
      p={1}
      justifyContent="center"
    >
      <CreateCID dialogOpen={dialogOpen} handleClose={toggleDialog} />
      <ConfirmDelete
        dialogOpen={confirmDialog}
        handleClose={toggleConfirmDialog}
        deleteCID={handleDelete}
      />
      <ModifyCID dialogOpen={modifyDialog} handleClose={toggleModifyDialog} />
      <Grid item xs={12} mx="auto">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Minimize />}
            onClick={() => props.toggleLookup()}
          >
            Hide Editor
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {form.cid ? (
              <Typography variant="body2" color="text.secondary">
                CID #{form.cid}
              </Typography>
            ) : null}
            {form.cid ? (
              <Tooltip title="Customize fields">
                <IconButton color="primary" onClick={openFieldsMenu}>
                  <Tune />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title="CID actions">
              <IconButton color="primary" onClick={openCidActions}>
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={fieldsMenuAnchorEl}
            open={fieldsMenuOpen}
            onClose={closeFieldsMenu}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, py: 0.5, display: "block" }}
            >
              Show fields in this editor
            </Typography>
            {EDITOR_FIELDS.map((field) => (
              <MenuItem
                key={field.key}
                dense
                onClick={() => toggleField(field.key)}
              >
                <Checkbox
                  edge="start"
                  size="small"
                  checked={isFieldVisible(field.key)}
                  tabIndex={-1}
                  disableRipple
                  sx={{ mr: 1, p: 0.25 }}
                />
                {field.label}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              dense
              disabled={hiddenFields.length === 0}
              onClick={showAllFields}
            >
              Show all fields
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={cidActionsAnchorEl}
            open={cidActionsOpen}
            onClose={closeCidActions}
          >
            {admin ? (
              <MenuItem
                onClick={() => {
                  closeCidActions();
                  toggleDialog(true);
                }}
              >
                <AddCircleOutline sx={{ mr: 1 }} />
                Create CID
              </MenuItem>
            ) : null}
            {admin ? (
              <MenuItem
                disabled={!form.cid}
                onClick={() => {
                  closeCidActions();
                  toggleModifyDialog(true);
                }}
              >
                <EditOutlined sx={{ mr: 1 }} />
                Modify CID
              </MenuItem>
            ) : null}
            {admin ? (
              <MenuItem
                disabled={!form.cid}
                onClick={() => {
                  closeCidActions();
                  toggleConfirmDialog(true);
                }}
              >
                <DeleteOutline sx={{ mr: 1 }} />
                Delete CID
              </MenuItem>
            ) : null}
          </Menu>
        </Box>
      </Grid>

      {!assessor.form.cid && searchStatus === 2 ? (
        <Alert sx={{ mt: 1 }} severity="error">
          Must select a CID
        </Alert>
      ) : (
        <></>
      )}

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>CID</InputLabel>
          <Select
            value={cidSelectValue}
            onChange={(e) => handleChangeCID(e)}
            label="CID"
            fullWidth
          >
            {assessor.cids !== null
              ? assessor.cids.map((element, index) => {
                  return (
                    <MenuItem key={index} value={element.id}>
                      {element.name}
                    </MenuItem>
                  );
                })
              : null}
          </Select>
        </FormControl>
      </Grid>
      {form.cid && isFieldVisible("filters") ? (
        <Grid item xs={12}>
          <FilterPanel />
        </Grid>
      ) : null}
      {dataLoading ? (
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      ) : (
        <></>
      )}

      <Grid item xs={12}>
        <TextField
          value={form.parcelID}
          fullWidth
          label="Parcel ID"
          onChange={(e) => dispatch(setParcelID(e.target.value))}
        />
      </Grid>
      <Grid item xs={12}>
        {/* One row of equal-width, equal-height buttons — `alignItems:
            stretch` keeps them aligned even when a label wraps to two lines
            in a narrow editor. */}
        <Stack direction="row" spacing={1} alignItems="stretch">
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              dispatch(resetForm());
            }}
          >
            Clear
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => startManualEntry()}
            type="submit"
          >
            Manual Entry
          </Button>
          {searchStatus === 2 ? (
            <Tooltip
              title={
                !cidWritable
                  ? "You have read-only access to this CID"
                  : isDirty
                    ? ""
                    : "No changes to save yet"
              }
            >
              {/* Box wrapper keeps the tooltip working while the button is
                  disabled, and flexes to the same width as its siblings. */}
              <Box sx={{ width: "100%", display: "flex" }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="warning"
                  onClick={() => onPush()}
                  disabled={!form.parcelID || !cidWritable || !isDirty}
                >
                  {form.transferID === -1
                    ? "Add Property Data"
                    : "Update Property Data"}
                </Button>
              </Box>
            </Tooltip>
          ) : null}
          {assessorUrl ? (
            <Button
              fullWidth
              variant="outlined"
              endIcon={<OpenInNew />}
              href={assessorUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Assessor Site
            </Button>
          ) : null}
        </Stack>
      </Grid>
      {searchStatus === 2 ? (
        <>
          {isFieldVisible("marker") ? (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel id="markerColor">Marker Color</FormLabel>

              <Select
                fullWidth
                labelId="markerColor"
                size="small"
                value={form.flagColor || ""}
                onChange={(e) => dispatch(setMarker(e.target.value || null))}
                renderValue={(selected) => {
                  const selectedOption =
                    markerOptions.find((option) => option.value === selected) || EMPTY_MARKER;
                  return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: selectedOption.color,
                        }}
                      />
                      <Typography variant="body2">{selectedOption.label}</Typography>
                    </Stack>
                  );
                }}
              >
                {markerOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          backgroundColor: option.color,
                        }}
                      />
                      <Typography variant="body2">{option.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
                <MenuItem value="">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: EMPTY_MARKER.color,
                      }}
                    />
                    <Typography variant="body2">{EMPTY_MARKER.label}</Typography>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          ) : null}
          {isFieldVisible("ownerName") ? (
          <Grid item xs={12}>
            <TextField
              onChange={(e) => dispatch(setOwnerName(e.target.value))}
              fullWidth
              label="Owner Name"
              value={form.ownerName}
            />
          </Grid>
          ) : null}
          {isFieldVisible("ownerMailing") ? (
          <Grid item xs={12}>
            <AddressField
              label="Owner Mailing Address"
              value={form.ownerMailing}
              onChange={(e) => dispatch(setOwnerMailing(e.target.value))}
              onPlace={({ address }) => dispatch(setOwnerMailing(address))}
            />
          </Grid>
          ) : null}
          {isFieldVisible("buildingAddress") ? (
          <Grid item xs={12}>
            <AddressField
              label="Building Address"
              value={form.buildingAddress}
              onChange={(e) => dispatch(setBuildingAddr(e.target.value))}
              // Typed edits invalidate stored coordinates so the map
              // re-geocodes (debounced) once typing pauses. Only fires when
              // maps are enabled — without a Maps key nothing could ever
              // re-geocode, and a save would persist the nulls.
              onUserEdit={() =>
                dispatch(setCoordinates({ latitude: null, longitude: null }))
              }
              onPlace={({ address, latitude, longitude }) => {
                dispatch(setBuildingAddr(address));
                dispatch(setCoordinates({ latitude, longitude }));
              }}
            />
          </Grid>
          ) : null}
          {isFieldVisible("map") ? (
          <Grid item xs={12}>
            <PropertyMap />
          </Grid>
          ) : null}
          {isFieldVisible("valuation") ? (
          <>
          <Grid item xs={12}>
            <FormControlLabel
              label="Exempt from tax"
              control={
                <Checkbox
                  color="error"
                  checked={form.exempt}
                  onChange={() => dispatch(setExempt(!form.exempt))}
                />
              }
            />
          </Grid>
          {form.exempt ? (
            <></>
          ) : (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Assessor Value"
                  type="number"
                  value={form.assessorValue}
                  onChange={(e) => {
                    if (!e.target.value) {
                      dispatch(setAssessorCIDValue(0));
                    } else {
                      dispatch(setAssessorCIDValue(parseFloat(e.target.value)));
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  // Match the plain look of the text fields above — drop the
                  // number spinners so it reads as a currency field.
                  sx={{
                    "& input[type=number]": { MozAppearance: "textfield" },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      { WebkitAppearance: "none", margin: 0 },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="CID Value"
                  value={form.cidValue.toFixed(2)}
                  helperText="Calculated from assessor value × mils"
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
          </>
          ) : null}

          {isFieldVisible("propertyDesc") ? (
          <Grid item xs={12} mx="auto">
            <TextField
              minRows={7}
              multiline
              fullWidth
              onChange={(e) => dispatch(setPropertyDesc(e.target.value))}
              label="Property Description"
              value={form.propertyDesc}
            />
          </Grid>
          ) : null}

          {isFieldVisible("customFields")
            ? propertyFields.map((def) => (
                <Grid item xs={12} key={def.id}>
                  <CustomFieldInput
                    def={def}
                    value={form.customFields?.[def.internal_name]}
                    onChange={(value) =>
                      dispatch(
                        setCustomFieldValue({ name: def.internal_name, value })
                      )
                    }
                  />
                </Grid>
              ))
            : null}

          {isFieldVisible("contacts") ? (
          <Grid item xs={12}>
            <Contacts mode="embedded" onContactChanged={refreshAssessorData} />
          </Grid>
          ) : null}
        </>
      ) : (
        <></>
      )}
    </Grid>
    </MapsProvider>
  );
};

export default LookupTool;
